export const coachesData = {
  industry: "Coaches",
  slug: "coaches",
  
  // SEO Meta Tags
  seo: {
    title: "AI Intake Forms for Coaches | HeyIntake",
    description: "AI-powered intake forms for life coaches, executive coaches, and business coaches. Screen for commitment, assess fit, and attract your ideal coaching clients.",
    keywords: [
      "coaching client intake form",
      "coach lead qualification",
      "coaching client screening",
      "executive coach intake",
      "life coach client onboarding",
      "coaching discovery call prep"
    ]
  },

  heroTitle: "Client Intake Forms for Coaching Professionals",
  heroSubtitle: "Understand your clients before the first session and attract more of your ideal coaching clients.",
  heroDescription: "Whether you're a life coach, executive coach, health coach, or business coach, HeyIntake helps you qualify prospects and prepare for transformative coaching relationships.",
  
  // NEW: Why AI Scoring section
  whyAIScoring: {
    title: "Why Coaches Need Better Client Screening",
    intro: "Great coaching outcomes depend on working with the right clients — people who are ready, committed, and aligned with your methodology.",
    paragraphs: [
      "Too many coaches accept clients who aren't truly ready for change. They're attracted to the idea of coaching but haven't committed to doing the work. The result: frustrating sessions, missed appointments, and poor outcomes that don't reflect your true capabilities.",
      "HeyIntake helps you identify commitment signals before the first session. Our AI-powered intake forms evaluate readiness indicators, goal clarity, and budget alignment. You'll know whether a prospect is genuinely motivated or just window shopping.",
      "For executive coaches, life coaches, health coaches, and business coaches, this means better client outcomes, higher retention rates, and more referrals from satisfied clients who were truly ready for transformation."
    ]
  },
  
  painPoints: [
    {
      title: "Mismatched Client Expectations",
      description: "Starting coaching relationships only to discover the client isn't ready or isn't a good fit for your approach."
    },
    {
      title: "Unprepared Discovery Calls",
      description: "Going into calls without understanding the client's background, goals, or what they've already tried."
    },
    {
      title: "Time Wasted on Non-Buyers",
      description: "Giving away too much in free consultations to people who were never going to invest."
    },
    {
      title: "No Commitment Screening",
      description: "Taking on clients who aren't truly committed to the work, leading to disappointing results."
    },
    {
      title: "Inconsistent Onboarding",
      description: "Different clients start with different levels of preparation, affecting early session quality."
    },
    {
      title: "Missing Important Context",
      description: "Learning crucial information about a client's situation sessions into the engagement."
    }
  ],

  exampleFields: [
    {
      category: "Personal Information",
      fields: [
        "Full name",
        "Email and phone",
        "Location and timezone",
        "How did you find me?",
        "Have you worked with a coach before?"
      ]
    },
    {
      category: "Goals & Challenges",
      fields: [
        "What do you most want to achieve?",
        "What's your biggest challenge right now?",
        "What have you already tried?",
        "What would change if you achieved this goal?",
        "On a scale of 1-10, how committed are you?"
      ]
    },
    {
      category: "Readiness & Fit",
      fields: [
        "Why now? What's prompted you to seek coaching?",
        "What does support look like for you?",
        "How do you handle accountability?",
        "What's your preferred communication style?"
      ]
    },
    {
      category: "Logistics",
      fields: [
        "Preferred session times",
        "Investment budget for coaching",
        "Ideal coaching duration",
        "Any scheduling constraints?"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Coaching Intake Form",
    description: "Here's what an effective coaching inquiry form looks like:",
    fields: [
      { label: "What type of coaching are you seeking?", type: "select", options: ["Executive/Leadership", "Career", "Life/Personal", "Business", "Health/Wellness", "Relationship"] },
      { label: "What's your primary goal?", type: "textarea" },
      { label: "Have you worked with a coach before?", type: "select", options: ["No", "Yes - good experience", "Yes - didn't work out"] },
      { label: "Preferred coaching format", type: "select", options: ["1-on-1 video calls", "In-person", "Group coaching", "Flexible"] },
      { label: "How often would you like to meet?", type: "select", options: ["Weekly", "Bi-weekly", "Monthly", "Intensive program"] },
      { label: "Monthly coaching budget", type: "select", options: ["$300-$500", "$500-$1,000", "$1,000-$2,000", "$2,000+"] },
      { label: "What does success look like in 6 months?", type: "textarea" }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // NEW: How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Coaches",
    subtitle: "From prospect to committed client in three simple steps",
    steps: [
      {
        title: "Prospects Share Their Story",
        description: "Potential clients complete your intake form with goals, challenges, and commitment indicators."
      },
      {
        title: "AI Assesses Readiness",
        description: "Our AI evaluates commitment level, goal clarity, and fit with your coaching approach."
      },
      {
        title: "Connect with Ideal Clients",
        description: "Focus on high-commitment prospects who are genuinely ready for transformation."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Client Qualification for Coaches",
    description: "HeyIntake's AI helps you identify clients who are ready, committed, and a great fit for your coaching style — before you invest time in a discovery call.",
    factors: [
      "Commitment level and readiness for change",
      "Clarity of goals and desired outcomes",
      "Budget alignment with your programs",
      "Coachability indicators and openness to feedback",
      "Timeline and urgency signals"
    ],
    exampleLead: {
      name: "Amanda Richards",
      context: "Career Transition • Executive Background • Ready to Invest",
      summary: "Clear goal (pivot to consulting), high commitment score, previous positive coaching experience, budget aligned. Excellent candidate for your executive program.",
      score: 94
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Screen for Commitment",
      description: "AI identifies clients who are truly ready to do the work, not just interested in the idea of coaching."
    },
    {
      icon: "users",
      title: "Prepare for Powerful Sessions",
      description: "Know your client's story, goals, and challenges before you ever meet, so you can dive deep from session one."
    },
    {
      icon: "zap",
      title: "Attract Your Ideal Clients",
      description: "Thoughtful intake questions help the right clients self-select while filtering out poor fits."
    },
    {
      icon: "clock",
      title: "Save Time on Discovery Calls",
      description: "Spend less time gathering background and more time exploring fit and building connection."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" },
    { name: "Intake Forms for Therapists", path: "/intake-form-for-therapists" },
    { name: "Intake Forms for Agencies", path: "/intake-form-for-agencies" }
  ],

  ctaText: "Ready to Attract More Committed Coaching Clients?"
};
